import axios from "axios";
import {
  ConversionRequest,
  ConversionResponse,
} from "../types/conversionTypes";

//#region 型定義
/** ファイルアップロードのレスポンス型 */
export interface FileUploadResponse {
  success: boolean;
  message: string;
  filePath: string;
  errorDetails?: string;
}

/** ファイル削除のレスポンス型 */
export interface FileDeleteResponse {
  success: boolean;
  message: string;
  deletedFilePath?: string;
  errorDetails?: string;
}

/** クリップボード出力のリクエスト型 */
export interface ClipboardOutputRequest {
  inputFilePath: string;
  mdlType: string;
  machineToolType: string;
  contourLineDisplayMode: boolean;
}

/** クリップボード出力のレスポンス型 */
export interface ClipboardOutputResponse {
  success: boolean;
  message: string;
  base64ImageData?: string;
  savedImagePath?: string;
  errorDetails?: string;
}
//#endregion

//#region 定数定義
/** APIのベースURL */
const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  console.error("環境変数 REACT_APP_API_URL が設定されていません");
}

/** タイムアウト時間（ミリ秒） */
const TIMEOUT = 60000;

/** 最大リトライ回数 */
const MAX_RETRIES = 3;

/** ベースの遅延時間（ミリ秒） */
const BASE_RETRY_DELAY = 1000;

/** APIクライアント */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: TIMEOUT,
});
//#endregion

//#region インターセプター
// リクエストインターセプターの追加
apiClient.interceptors.request.use(
  (config) => {
    console.log("リクエスト送信:", config.url);
    return config;
  },
  (error) => {
    console.error("リクエストエラー:", error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプターの追加
apiClient.interceptors.response.use(
  (response) => {
    console.log("レスポンス受信:", response.status);
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      console.error("APIレスポンスエラー:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    return Promise.reject(error);
  }
);
//#endregion

//#region 変換APIクライアント
export const conversionApi = {
  //#region ファイルアップロード
  /**
   * ファイルをアップロードする
   * @param file アップロードするファイル
   * @returns アップロード結果
   */
  uploadFile: async (file: File): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post("/api/File/uploadNcFile", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 100)
            );
            console.log(`アップロード進捗: ${percentCompleted}%`);
          },
        });
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("アップロードに失敗しました");
  },
  //#endregion

  //#region ファイル変換
  /**
   * ファイル変換を実行する
   * @param request 変換リクエストパラメータ
   * @returns 変換結果
   */
  convertFile: async (
    request: ConversionRequest
  ): Promise<ConversionResponse> => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post<ConversionResponse>(
          "/api/FileConversion",
          request
        );
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("変換に失敗しました");
  },
  //#endregion

  //#region ファイルダウンロード
  /**
   * ファイルをダウンロードする
   * @param filePath ダウンロードするファイルのパス
   * @returns ファイルのBlob
   */
  downloadFile: async (filePath: string): Promise<Blob> => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.get("/api/File/download", {
          params: { filePath },
          responseType: "blob",
        });
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("ダウンロードに失敗しました");
  },
  //#endregion

  //#region クリップボード出力
  /**
   * NCファイルをクリップボードに画像として出力する
   * @param request クリップボード出力リクエスト
   * @returns 出力結果
   */
  outputToClipboard: async (
    request: ClipboardOutputRequest
  ): Promise<ClipboardOutputResponse> => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiClient.post<ClipboardOutputResponse>(
          "/api/FileConversion/clipboard",
          request
        );
        return response.data;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("クリップボード出力に失敗しました");
  },
  //#endregion

  //#region ファイル削除
  /**
   * アップロードされたファイルを削除する
   * @param filePath 削除するファイルのパス
   */
  deleteFile: async (filePath: string): Promise<void> => {
    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        await apiClient.delete("/api/File/delete", {
          params: { filePath },
        });
        return;
      } catch (err) {
        retryCount++;
        if (retryCount === MAX_RETRIES) throw err;
        await new Promise((resolve) =>
          setTimeout(resolve, BASE_RETRY_DELAY * retryCount)
        );
      }
    }
    throw new Error("ファイル削除に失敗しました");
  },
  //#endregion
};
//#endregion
